import { useEffect, useState } from 'react';
import { useAuth } from '@helios/auth-firebase-web';
import { usePowerSyncDb, type AppConfigRecord } from '../adapters/powersync';

/**
 * Phase 1 verification page — rendered inside CommonShellLayout canvas.
 *
 * Displays:
 * - Auth status (user email, activeOrg, roles)
 * - PowerSync connection status
 * - app_config rows from local PowerSync database
 * - Raw claims (collapsible)
 */
export function SystemInfoPage() {
  const { user, claims, signOut } = useAuth();
  const { db, isConnected, isSyncing, error: psError } = usePowerSyncDb();

  const [appConfigs, setAppConfigs] = useState<AppConfigRecord[]>([]);
  const [rowCount, setRowCount] = useState<number>(0);
  const [queryError, setQueryError] = useState<string | null>(null);
  const [claimsOpen, setClaimsOpen] = useState(false);

  // Debug logging for PowerSync state
  console.log('[HomePage] PowerSync state:', {
    hasDb: !!db,
    isConnected,
    isSyncing,
    hasError: !!psError,
    error: psError?.message,
  });

  // Get roles for active org
  const activeOrg = claims?.activeOrg;
  const orgRoles = (activeOrg && claims?.orgs?.[activeOrg]?.roles) || [];

  // Query app_config when db is ready
  useEffect(() => {
    if (!db) return;

    const database = db;

    async function queryAppConfig() {
      try {
        const result = await database.getAll<AppConfigRecord>(
          'SELECT * FROM app_config ORDER BY updated_at DESC LIMIT 10',
        );
        setAppConfigs(result);
        setRowCount(result.length);
        setQueryError(null);
      } catch (err) {
        console.error('[HomePage] Query error:', err);
        setQueryError(err instanceof Error ? err.message : String(err));
      }
    }

    queryAppConfig();

    const unsubscribe = database.registerListener({
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      statusChanged: (status: any) => {
        if (status.connected && !status.dataFlowStatus.downloading) {
          queryAppConfig();
        }
      },
    });

    return () => {
      unsubscribe();
    };
  }, [db]);

  return (
    <>
      {/* Header row */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <h2
          style={{
            fontSize: 'var(--font-size-lg)',
            fontWeight: 'var(--font-weight-semi)',
            lineHeight: 'var(--line-height-lg)',
            letterSpacing: '-0.44px',
            color: 'var(--text)',
            margin: 0,
          }}
        >
          System Info
        </h2>
        <button
          onClick={() => signOut()}
          style={{
            height: 'var(--input-height)',
            padding: '0 var(--input-padding-x)',
            borderRadius: 'var(--radius-md)',
            border: '1px solid var(--color-danger-border)',
            background: 'var(--bg-surface)',
            color: 'var(--color-danger)',
            fontSize: 'var(--font-size-base)',
            fontWeight: 'var(--font-weight-medium)',
            cursor: 'pointer',
          }}
        >
          Sign Out
        </button>
      </div>

      {/* Auth + PowerSync — 2-col grid on desktop, stack on mobile */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
          gap: 'var(--sp-6)',
        }}
      >
        {/* Auth Status Card */}
        <Card title="Auth Status">
          <dl
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 'var(--sp-4)',
              margin: 0,
            }}
          >
            <FieldPair label="User Email" value={user?.email || '—'} mono />
            <FieldPair label="User ID" value={user?.uid || '—'} mono />
            <FieldPair label="Active Org" value={activeOrg || '—'} mono />
            <div>
              <dt style={labelStyle}>Roles (active org)</dt>
              <dd style={{ margin: 0 }}>
                {orgRoles.length > 0 ? (
                  <div style={{ display: 'flex', gap: 'var(--sp-1)', flexWrap: 'wrap' }}>
                    {orgRoles.map((role: string) => (
                      <span
                        key={role}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          padding: '2px 8px',
                          borderRadius: 'var(--radius-xs)',
                          background: 'var(--bg-primary-subtle)',
                          color: 'var(--color-primary)',
                          fontSize: 'var(--font-size-sm)',
                          fontWeight: 'var(--font-weight-medium)',
                        }}
                      >
                        {role}
                      </span>
                    ))}
                  </div>
                ) : (
                  <span style={valueStyle}>—</span>
                )}
              </dd>
            </div>
          </dl>
        </Card>

        {/* PowerSync Status Card */}
        <Card title="PowerSync Status">
          <dl
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 'var(--sp-4)',
              margin: 0,
            }}
          >
            <div>
              <dt style={labelStyle}>Connection</dt>
              <dd style={{ margin: 0 }}>
                <StatusBadge
                  status={isConnected ? 'success' : 'pending'}
                  label={isConnected ? 'Connected' : 'Disconnected'}
                />
              </dd>
            </div>
            <div>
              <dt style={labelStyle}>Sync State</dt>
              <dd style={{ margin: 0 }}>
                <StatusBadge
                  status={isSyncing ? 'syncing' : isConnected ? 'idle' : 'pending'}
                  label={isSyncing ? 'Syncing...' : isConnected ? 'Idle' : 'Waiting'}
                />
              </dd>
            </div>
            <div style={{ minWidth: 0 }}>
              <dt style={labelStyle}>Error</dt>
              <dd
                title={psError ? psError.message : undefined}
                style={{
                  margin: 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {psError ? (
                  <span
                    style={{
                      color: 'var(--color-danger)',
                      fontFamily: 'monospace',
                      fontSize: 'var(--font-size-sm)',
                    }}
                  >
                    {psError.message}
                  </span>
                ) : (
                  <span
                    style={{ color: 'var(--color-success)', fontSize: 'var(--font-size-base)' }}
                  >
                    None
                  </span>
                )}
              </dd>
            </div>
          </dl>
        </Card>
      </div>

      {/* app_config — full width */}
      <Card
        title="app_config (Local DB)"
        trailing={
          <span style={{ fontSize: 'var(--font-size-base)', color: 'var(--text-muted)' }}>
            {rowCount} row{rowCount !== 1 ? 's' : ''}
          </span>
        }
      >
        {queryError && (
          <div
            style={{
              marginBottom: 'var(--sp-4)',
              padding: 'var(--sp-3)',
              background: 'var(--color-danger-bg)',
              border: '1px solid var(--color-danger-border)',
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--font-size-base)',
              color: 'var(--color-danger-text)',
            }}
          >
            Query Error: {queryError}
          </div>
        )}

        {!db && (
          <div
            style={{
              fontSize: 'var(--font-size-base)',
              color: 'var(--text-muted)',
              fontStyle: 'italic',
            }}
          >
            Initializing PowerSync...
          </div>
        )}

        {db && appConfigs.length === 0 && !queryError && (
          <div
            style={{
              fontSize: 'var(--font-size-base)',
              color: 'var(--text-muted)',
              fontStyle: 'italic',
            }}
          >
            No rows in app_config. Waiting for sync...
          </div>
        )}

        {appConfigs.length > 0 && (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  {['ID', 'Org Name', 'Country', 'Updated At'].map((col) => (
                    <th
                      key={col}
                      style={{
                        padding: 'var(--sp-2) var(--sp-3)',
                        textAlign: 'left',
                        fontSize: 'var(--font-size-sm)',
                        fontWeight: 'var(--font-weight-medium)',
                        color: 'var(--text-muted)',
                        textTransform: 'uppercase',
                        letterSpacing: '0.6px',
                        borderBottom: '1px solid var(--border)',
                      }}
                    >
                      {col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {appConfigs.map((config) => (
                  <tr key={config.id}>
                    <td style={cellStyle}>{config.id}</td>
                    <td style={cellStyle}>{config.org_name || '—'}</td>
                    <td style={cellStyle}>{config.country || '—'}</td>
                    <td
                      style={{ ...cellStyle, color: 'var(--text-muted)', fontFamily: 'monospace' }}
                    >
                      {config.updated_at || '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {appConfigs.length > 0 && (
          <details style={{ marginTop: 'var(--sp-4)' }}>
            <summary
              style={{
                fontSize: 'var(--font-size-base)',
                color: 'var(--text-muted)',
                cursor: 'pointer',
              }}
            >
              View JSON
            </summary>
            <pre
              style={{
                marginTop: 'var(--sp-2)',
                padding: 'var(--sp-3)',
                background: 'var(--bg-surface-2)',
                borderRadius: 'var(--radius-md)',
                fontSize: 'var(--font-size-sm)',
                overflow: 'auto',
                fontFamily: 'monospace',
              }}
            >
              {JSON.stringify(appConfigs, null, 2)}
            </pre>
          </details>
        )}
      </Card>

      {/* Raw Claims — collapsible */}
      <Card title="Raw Claims">
        <button
          onClick={() => setClaimsOpen(!claimsOpen)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--sp-2)',
            padding: 'var(--sp-2) var(--sp-3)',
            borderRadius: 'var(--radius-md)',
            border: '1px solid var(--border)',
            background: 'var(--bg-surface)',
            fontSize: 'var(--font-size-base)',
            fontWeight: 'var(--font-weight-medium)',
            color: 'var(--text-muted)',
            cursor: 'pointer',
            marginBottom: claimsOpen ? 'var(--sp-3)' : 0,
          }}
        >
          {claimsOpen ? 'Hide' : 'Show'} Claims JSON
        </button>
        {claimsOpen && (
          <pre
            style={{
              padding: 'var(--sp-3)',
              background: 'var(--bg-surface-2)',
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--font-size-sm)',
              overflow: 'auto',
              maxHeight: 320,
              fontFamily: 'monospace',
              margin: 0,
            }}
          >
            {claims ? JSON.stringify(claims, null, 2) : 'No claims'}
          </pre>
        )}
      </Card>
    </>
  );
}

// ─── Shared sub-components ───

function Card({
  title,
  trailing,
  children,
}: {
  title: string;
  trailing?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section
      style={{
        background: 'var(--bg-surface)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-lg2)',
        boxShadow: 'var(--shadow-sm)',
        padding: 'var(--sp-6)',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 'var(--sp-4)',
        }}
      >
        <h3
          style={{
            fontSize: 'var(--font-size-lg)',
            fontWeight: 'var(--font-weight-semi)',
            lineHeight: 'var(--line-height-lg)',
            letterSpacing: '-0.44px',
            color: 'var(--text)',
            margin: 0,
          }}
        >
          {title}
        </h3>
        {trailing}
      </div>
      {children}
    </section>
  );
}

function FieldPair({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div style={{ minWidth: 0 }}>
      <dt style={labelStyle}>{label}</dt>
      <dd
        title={value}
        style={{
          ...valueStyle,
          fontFamily: mono ? 'monospace' : undefined,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          margin: 0,
        }}
      >
        {value}
      </dd>
    </div>
  );
}

function StatusBadge({
  status,
  label,
}: {
  status: 'success' | 'pending' | 'syncing' | 'idle';
  label: string;
}) {
  const colors: Record<string, { bg: string; text: string }> = {
    success: { bg: 'var(--color-success-bg)', text: 'var(--color-success-text)' },
    pending: { bg: 'var(--color-warning-bg)', text: 'var(--color-warning-text)' },
    syncing: { bg: 'var(--color-info-bg)', text: 'var(--color-info)' },
    idle: { bg: 'var(--bg-surface-2)', text: 'var(--text-muted)' },
  };

  const c = colors[status];

  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: '2px 10px',
        borderRadius: 'var(--radius-full)',
        background: c.bg,
        color: c.text,
        fontSize: 'var(--font-size-sm)',
        fontWeight: 'var(--font-weight-medium)',
      }}
    >
      {label}
    </span>
  );
}

// ─── Shared styles ───

const labelStyle: React.CSSProperties = {
  fontSize: 'var(--font-size-base)',
  fontWeight: 'var(--font-weight-medium)',
  color: 'var(--text-muted)',
  marginBottom: 'var(--sp-1)',
};

const valueStyle: React.CSSProperties = {
  fontSize: 'var(--font-size-base)',
  color: 'var(--text)',
};

const cellStyle: React.CSSProperties = {
  padding: 'var(--sp-2) var(--sp-3)',
  fontSize: 'var(--font-size-base)',
  color: 'var(--text)',
  borderBottom: '1px solid var(--border)',
  fontFamily: 'monospace',
};
